/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useState, useEffect } from 'react';
import { DeviceInfo, getDeviceInfo } from '../contexts/videoProvider/utils';
import { uniqueId } from '@utils/lodash';
import { diagnostics } from '@utils/diagnostics';

export function useDevices(localTracks?: VideoProvider.ITrack[]) {
  const [devices, setDevices] = useState<DeviceInfo>({
    audioInputDevices: [],
    videoInputDevices: [],
    audioOutputDevices: [],
    hasAudioInputDevices: false,
    hasVideoInputDevices: false,
  });

  useEffect(() => {
    let isMounted = true;
    const getDevices = () => getDeviceInfo().then(devices => isMounted && setDevices(devices));
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    // TODO: (ZOOMSDK) - investigate if this is needed for zoom
    getDevices();

    return () => {
      isMounted = false;
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, [localTracks]);

  return devices;
}

export function useMediaPermissions() {
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);

  useEffect(() => {
    const checkDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputDevices = devices.filter(device => device.kind === 'videoinput' && device.label);
        const audioInputDevices = devices.filter(device => device.kind === 'audioinput' && device.label);

        setVideoEnabled(videoInputDevices.length > 0);
        setAudioEnabled(audioInputDevices.length > 0);
      } catch (err) {
        diagnostics.error(new Error('Error enumerating devices'));
        setVideoEnabled(false);
        setAudioEnabled(false);
      }
    };

    checkDevices();
    const intervalId = setInterval(checkDevices, 1000);

    return () => clearInterval(intervalId);
  }, [videoEnabled, audioEnabled]);

  return { videoEnabled, audioEnabled };
}

export function useAudioInputDevices(localTracks: VideoProvider.ITrack[]) {
  const devices = useDevices(localTracks);
  return devices.audioInputDevices;
}

export function useVideoInputDevices(localTracks: VideoProvider.ITrack[]) {
  const devices = useDevices(localTracks);
  return devices.videoInputDevices;
}

export function useAudioOutputDevices(localTracks: VideoProvider.ITrack[]): MediaDeviceInfo[] {
  const devices = useDevices(localTracks);
  const detectedDevices = devices.audioOutputDevices;
  if (detectedDevices.length !== 0) {
    return detectedDevices;
  }
  const systemDefaultAudioOutput = {
    deviceId: 'default',
    groupId: uniqueId(),
    kind: 'audiooutput',
    label: 'System Default',
    toJSON: () => 'System Default',
  } as const;
  return [systemDefaultAudioOutput];
}
